import { Player } from "./player.js";

// Turn advancement as a pure function so the ordering rules can be tested without a
// DOM. The loop is bounded by the roster size: an unbounded do/while could otherwise
// hand the turn to an eliminated player and stall the match.
export function advanceActive({ players = [], active = 0, round = 1 } = {}, isDead = Player.isDead) {
  if (!Array.isArray(players) || players.length === 0) return { active: 0, round };

  const anyLiving = players.some((player) => !isDead(player));
  let nextActive = active;
  let nextRound = round;

  for (let step = 0; step < players.length; step += 1) {
    nextActive = (nextActive + 1) % players.length;
    if (nextActive === 0) nextRound += 1;
    if (!anyLiving || !isDead(players[nextActive])) break;
  }

  return { active: nextActive, round: nextRound };
}

// The match ends only when at most one player is left standing. Running out of cards
// no longer ends anything — fatigue grinds card-less players down until it does.
// The winner is whoever drank least.
export function matchOutcome({ players = [] } = {}, isDead = Player.isDead) {
  if (!Array.isArray(players) || players.length === 0) return { ended: false, winner: null };

  const living = players.filter((player) => !isDead(player));
  if (living.length > 1) return { ended: false, winner: null };

  const winner = [...players].sort((a, b) => a.sipsTaken - b.sipsTaken)[0];
  return { ended: true, winner };
}

// Dropping a player mid-match has to keep `active` pointing at the same seat, or the
// turn silently jumps to whoever slid into the removed index.
export function withoutPlayer({ players = [], active = 0 } = {}, playerId) {
  if (!Array.isArray(players)) return { players: [], active: 0 };

  const index = players.findIndex((player) => player?.id === playerId);
  if (index < 0) return { players: [...players], active };

  const remaining = players.filter((_, position) => position !== index);
  if (!remaining.length) return { players: remaining, active: 0 };

  let nextActive = index < active ? active - 1 : active;
  if (nextActive < 0 || nextActive >= remaining.length) nextActive = 0;
  return { players: remaining, active: nextActive };
}
